import { useParams } from 'react-router-dom';
import { Form, FormProps } from 'components';
import { getUpdatedPayload } from 'utils/helpers';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, useSupplierManagementReadQuery, useSupplierManagementUpdateMutation } from 'api';
import { IManagementUpdate } from './index';

interface SupplierManagementFieldType {
  supplierId: string;
  purchaseManager: string;
  postSalesManager: string;
  postSalesAssistantManager: string;
  purchaseAssistantManager: string;
}

export const useEditManagement = (props: IManagementUpdate) => {
  const { onCancel, openUpdateModal } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: userData = [] } = useAdminUsersQuery();
  const { data: supplierManagement = {} } = useSupplierManagementReadQuery({ supplierId: id });
  const [updateManagement, { isLoading }] = useSupplierManagementUpdateMutation();

  const initialManagement: Record<string, string> = useMemo(
    () => ({
      purchaseManagerId: supplierManagement.purchaseManager?.id,
      postSalesManagerId: supplierManagement.postSalesManager?.id,
      postSalesManagerAssistantId: supplierManagement.postSalesManagerAssistant?.id,
      purchaseManagerAssistantId: supplierManagement.purchaseManagerAssistant?.id,
    }),
    [supplierManagement]
  );

  const onModalCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const onFinish: FormProps<SupplierManagementFieldType>['onFinish'] = useCallback(
    async (values: SupplierManagementFieldType) => {
      try {
        const payload = getUpdatedPayload(values, initialManagement);
        payload.supplierId = id as string;
        const { data, error }: Record<string, any> = await updateManagement(payload);
        if (data?.success) {
          handleSuccess('Supplier management was successfully updated.');
          onCancel();
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [updateManagement, handleError, handleSuccess, id, onCancel, initialManagement]
  );

  useEffect(() => {
    if (initialManagement && openUpdateModal) {
      form.setFieldsValue(initialManagement);
    }
  }, [initialManagement, openUpdateModal, form]);

  return {
    onFinish,
    userData,
    form,
    isLoading,
    onModalCancel,
  };
};
